import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Input } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Calendar, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../../../contexts/authContext';
import { useDeleteProposalMilestone, useProposalMilestones, useUpsertProposalMilestone } from './milestoneHooks';
// Compare UTC calendar dates so "today" is correct regardless of time-of-day.
export function utcDaysLeft(isoString) {
    const milestoneDay = isoString.slice(0, 10);
    const todayDay = new Date().toISOString().slice(0, 10);
    return Math.round((new Date(milestoneDay).getTime() - new Date(todayDay).getTime()) / 86400000);
}
const MILESTONE_TYPE_LABELS = {
    proposal_due: 'Proposal Due',
    questions_due: 'Questions Due',
    oral_presentation: 'Oral Presentation',
    colour_team_pink: 'Pink Team',
    colour_team_red: 'Red Team',
    colour_team_gold: 'Gold Team',
    internal_draft: 'Internal Draft',
    other: 'Other'
};
const MILESTONE_TYPES = Object.keys(MILESTONE_TYPE_LABELS);
const TYPE_COLOURS = {
    proposal_due: 'bg-rose-100 text-rose-700',
    questions_due: 'bg-amber-100 text-amber-700',
    oral_presentation: 'bg-purple-100 text-purple-700',
    colour_team_pink: 'bg-pink-100 text-pink-700',
    colour_team_red: 'bg-red-100 text-red-700',
    colour_team_gold: 'bg-yellow-100 text-yellow-800',
    internal_draft: 'bg-sky-100 text-sky-700',
    other: 'bg-slate-100 text-slate-600'
};
const EMPTY = {
    milestoneType: 'internal_draft',
    label: '',
    scheduledAt: ''
};
export function MilestonesPanel({ projectId }) {
    const { user } = useAuth();
    const hasIdentity = !!user?.tenant_id;
    const { data: rawMilestones = [], isLoading, isError } = useProposalMilestones(projectId);
    const milestones = useMemo(()=>[
            ...rawMilestones
        ].sort((a, b)=>{
            if (!a.scheduledAt) return 1;
            if (!b.scheduledAt) return -1;
            return a.scheduledAt.localeCompare(b.scheduledAt);
        }), [
        rawMilestones
    ]);
    const upsert = useUpsertProposalMilestone(projectId);
    const removeM = useDeleteProposalMilestone(projectId);
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [deletingIds, setDeletingIds] = useState(new Set());
    function set(field, value) {
        setForm((prev)=>({
                ...prev,
                [field]: value
            }));
    }
    async function submit() {
        if (!form.label.trim() || !form.scheduledAt || !hasIdentity) return;
        try {
            await upsert.mutateAsync({
                id: '',
                projectId,
                milestoneType: form.milestoneType,
                label: form.label.trim(),
                scheduledAt: form.scheduledAt,
                source: 'user'
            });
            setForm(EMPTY);
            setAdding(false);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to save milestone');
        }
    }
    async function remove(id) {
        if (!hasIdentity) return;
        setDeletingIds((s)=>new Set(s).add(id));
        try {
            await removeM.mutateAsync(id);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to delete milestone');
        } finally{
            setDeletingIds((s)=>{
                const next = new Set(s);
                next.delete(id);
                return next;
            });
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        id: "proposal-milestones",
        className: "space-y-3",
        "data-testid": Testids.milestones.panel,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5 text-sm font-medium",
                        children: [
                            /*#__PURE__*/ _jsx(Calendar, {
                                className: "h-4 w-4 text-muted-foreground",
                                "aria-hidden": "true"
                            }),
                            "Milestones"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        size: "sm",
                        variant: "ghost",
                        "data-testid": Testids.milestones.addButton,
                        onClick: ()=>setAdding((v)=>!v),
                        children: [
                            /*#__PURE__*/ _jsx(Plus, {
                                className: "mr-1 h-3.5 w-3.5",
                                "aria-hidden": "true"
                            }),
                            "Add"
                        ]
                    })
                ]
            }),
            adding && /*#__PURE__*/ _jsxs("div", {
                className: "rounded-md border p-3 space-y-2 text-sm",
                "data-testid": Testids.milestones.form,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("select", {
                                "aria-label": "Milestone type",
                                className: "h-8 rounded-md border border-input bg-background px-2 text-sm",
                                value: form.milestoneType,
                                onChange: (e)=>set('milestoneType', e.target.value),
                                children: MILESTONE_TYPES.map((t)=>/*#__PURE__*/ _jsx("option", {
                                        value: t,
                                        children: MILESTONE_TYPE_LABELS[t]
                                    }, t))
                            }),
                            /*#__PURE__*/ _jsx(Input, {
                                "aria-label": "Milestone label",
                                className: "h-8 flex-1",
                                placeholder: "Label",
                                value: form.label,
                                onChange: (e)=>set('label', e.target.value)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex gap-2",
                        children: /*#__PURE__*/ _jsx(Input, {
                            type: "date",
                            "aria-label": "Scheduled date",
                            className: "h-8",
                            value: form.scheduledAt,
                            onChange: (e)=>set('scheduledAt', e.target.value)
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                variant: "ghost",
                                disabled: upsert.isPending,
                                onClick: ()=>setAdding(false),
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "sm",
                                "data-testid": Testids.milestones.saveButton,
                                disabled: !form.label.trim() || !form.scheduledAt || !hasIdentity || upsert.isPending,
                                onClick: submit,
                                children: "Save"
                            })
                        ]
                    })
                ]
            }),
            isLoading || !!projectId && !hasIdentity ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Loading…"
            }) : isError ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-destructive",
                children: "Failed to load milestones."
            }) : milestones.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "No milestones yet — add one above."
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1.5",
                "data-testid": Testids.milestones.list,
                children: milestones.map((m)=>{
                    const colour = TYPE_COLOURS[m.milestoneType] ?? TYPE_COLOURS.other;
                    const daysLeft = m.scheduledAt ? utcDaysLeft(m.scheduledAt) : null;
                    const overdue = daysLeft !== null && daysLeft < 0;
                    return /*#__PURE__*/ _jsxs("li", {
                        "data-testid": Testids.milestones.item,
                        className: "flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 min-w-0",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${colour}`,
                                        children: MILESTONE_TYPE_LABELS[m.milestoneType] ?? m.milestoneType
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "truncate font-medium",
                                        children: m.label
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex shrink-0 items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: `text-xs tabular-nums ${overdue ? 'text-rose-600 font-medium' : 'text-muted-foreground'}`,
                                        children: [
                                            m.scheduledAt ? new Date(m.scheduledAt).toLocaleDateString('en-US', {
                                                timeZone: 'UTC',
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            }) : 'No date',
                                            daysLeft === null ? '' : overdue ? ` (${Math.abs(daysLeft)}d overdue)` : daysLeft === 0 ? ' (today)' : ` (${daysLeft}d)`
                                        ]
                                    }),
                                    m.source === 'user' && /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        "aria-label": `Delete ${m.label}`,
                                        className: "text-muted-foreground hover:text-destructive",
                                        disabled: !hasIdentity || deletingIds.has(m.id),
                                        onClick: ()=>remove(m.id),
                                        children: /*#__PURE__*/ _jsx(Trash2, {
                                            className: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                        })
                                    })
                                ]
                            })
                        ]
                    }, m.id);
                })
            })
        ]
    });
}
